import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { ApiError, unwrap } from '@/api/envelope';
import { updateOrganization } from '@/api/generated';
import type { OrganizationStatus } from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Dialog } from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/useToast';

import { STATUS_LABELS } from '@/lib/organization';
import { testIdAttr, testIdPart } from '@/lib/testIds';
import {
  REASON_LIMIT,
  STATUS_EFFECTS,
  STATUS_ORDER,
  reasonIsRequired,
} from './statusEffects';
import { storeSavedOrganization } from './organizationCache';

/** As much of an organization as changing its status needs to know. */
export interface StatusTarget {
  id: string;
  name: string;
  status: string;
}

interface ChangeStatusDialogProps {
  organization: StatusTarget;
  open: boolean;
  /** Called when the dialog has closed, whether or not anything changed. */
  onClose: () => void;
}

function label(status: string) {
  return STATUS_LABELS[status] ?? status;
}

/**
 * Moves one organization between the four statuses, with a reason.
 *
 * Rendered by both places staff look at an account - the account page and a row
 * in the list - so the move works the same way from either, and there is one
 * request path, one error handler and one set of wording to keep true.
 *
 * Controlled rather than self-opening: a dialog holding a form has to stay open
 * when the save fails, so that nothing typed is lost, and close when it
 * succeeds. Only the thing doing the saving knows which happened.
 */
export function ChangeStatusDialog({
  organization,
  open,
  onClose,
}: ChangeStatusDialogProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const id = useId();
  const counterId = `${id}-counter`;
  const requiredId = `${id}-required`;

  const [choice, setChoice] = useState<OrganizationStatus | null>(null);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /*
   * Start clean every time it opens.
   *
   * Adjusted while rendering rather than in an effect. React re-runs this
   * render before showing anything, so nothing from the last time is ever on
   * screen; an effect would paint the old contents first and then clear them.
   * Clearing on close instead would empty the boxes while the dialog is still
   * fading out, which reads as the work being thrown away rather than finished.
   */
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setChoice(null);
      setReason('');
      setConfirming(false);
      setFailure(null);
    }
  }

  const save = useMutation({
    mutationFn: async (status: OrganizationStatus) => {
      const trimmed = reason.trim();
      const reply = await updateOrganization({
        path: { organization_id: organization.id },
        body: { status, ...(trimmed ? { status_reason: trimmed } : {}) },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFailure(null),
    onSuccess: async (record) => {
      /*
       * Write what the backend returned into the cache before invalidating. The
       * account page's header and card read this record, and waiting for the
       * refetch left the old status on screen for the length of the round trip.
       * The edit page learned the same lesson with a renamed account.
       */
      storeSavedOrganization(queryClient, record);
      // The confirmation goes with it. Left open, it would be the only thing on
      // screen after the dialog underneath had closed.
      setConfirming(false);
      onClose();
      toast.show({
        message: `${record.name} is now ${label(record.status).toLowerCase()}.`,
      });
      // By prefix, so the rows, the summary counts and the account all go with
      // it.
      await queryClient.invalidateQueries({ queryKey: ['staff', 'organizations'] });
    },
    onError: (error) => {
      setConfirming(false);
      setFailure(
        // A 409 here is the backend explaining that the name is taken and one
        // of the two has to be renamed. That is exactly what staff need to
        // hear, so it is shown as written rather than replaced.
        error instanceof ApiError
          ? (error.serverMessage ?? error.message)
          : 'The status could not be changed. Please try again.',
      );
    },
  });

  const needsReason = reasonIsRequired(choice);
  const reasonMissing = needsReason && reason.trim().length === 0;
  const deactivating = choice === 'DEACTIVATED';
  const canApply = choice !== null && !reasonMissing && !save.isPending;

  function apply() {
    if (choice === null) return;
    // Deactivation is the one move that reads as an ending, so it is the one
    // that asks twice. Suspension is reversible and says so in its summary;
    // asking every time would only train people to click through.
    if (deactivating) {
      setConfirming(true);
      return;
    }
    save.mutate(choice);
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        testId="org-status-dialog"
        title={`Change status: ${organization.name}`}
        description={`This organization is currently ${label(organization.status).toLowerCase()}.`}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={save.isPending}
              data-testid="org-status-dialog-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant={deactivating ? 'danger' : 'primary'}
              onClick={apply}
              disabled={!canApply}
              loading={save.isPending}
              data-testid="org-status-dialog-apply-button"
            >
              {/* The words carry the danger, not only the colour. */}
              {deactivating ? 'Deactivate organization' : 'Apply'}
            </Button>
          </>
        }
      >
        {failure ? (
          <Alert
            severity="error"
            role="alert"
            data-testid="org-status-dialog-error"
            sx={{ mb: 3 }}
          >
            {failure}
          </Alert>
        ) : null}

        <FormControl sx={{ mb: 3 }}>
          <FormLabel id={`${id}-statuses`}>New status</FormLabel>
          <RadioGroup
            aria-labelledby={`${id}-statuses`}
            data-testid="org-status-dialog-status-group"
            value={choice ?? ''}
            onChange={(event) => setChoice(event.target.value as OrganizationStatus)}
          >
            {STATUS_ORDER.map((status) => (
              <FormControlLabel
                key={status}
                value={status}
                control={
                  <Radio
                    slotProps={{
                      input: testIdAttr(
                        `org-status-dialog-status-${testIdPart(status)}-radio`,
                      ),
                    }}
                  />
                }
                // The status it already has is shown rather than hidden, so the
                // four always read as the same four in the same order.
                disabled={status === organization.status}
                label={
                  status === organization.status
                    ? `${label(status)} (current)`
                    : label(status)
                }
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* Visible and announced. A screen-reader user hears the consequence
            change as they move down the statuses, rather than having to go and
            find it after choosing. */}
        <Box
          role="status"
          aria-live="polite"
          data-testid="org-status-dialog-effect-message"
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 1,
            bgcolor: 'action.hover',
            minHeight: 72,
          }}
        >
          <Typography variant="body2">
            {choice
              ? STATUS_EFFECTS[choice]
              : 'Choose a status to see what it does to this organization.'}
          </Typography>
        </Box>

        <TextField
          label={needsReason ? 'Reason (required)' : 'Reason (optional)'}
          value={reason}
          onChange={(event) => setReason(event.target.value.slice(0, REASON_LIMIT))}
          multiline
          minRows={3}
          fullWidth
          required={needsReason}
          slotProps={{
            htmlInput: {
              ...testIdAttr('org-status-dialog-reason-input'),
              maxLength: REASON_LIMIT,
              'aria-describedby': needsReason ? `${requiredId} ${counterId}` : counterId,
            },
          }}
        />
        {needsReason ? (
          <Typography
            id={requiredId}
            data-testid="org-status-dialog-reason-hint"
            variant="caption"
            component="p"
            sx={{ mt: 0.5, color: 'text.secondary' }}
          >
            A reason is required when suspending or deactivating an organization. It is
            kept on the account.
          </Typography>
        ) : null}
        <Typography
          id={counterId}
          data-testid="org-status-dialog-reason-count"
          variant="caption"
          component="p"
          sx={{ mt: 0.5, color: 'text.secondary' }}
        >
          {reason.length} of {REASON_LIMIT} characters
        </Typography>
      </Dialog>

      <ConfirmDialog
        open={confirming}
        testId="org-status-confirm-dialog"
        destructive
        title="Deactivate this organization?"
        description={`This treats the account as ended and frees its name for another organization. Deactivate ${organization.name}?`}
        confirmLabel="Deactivate organization"
        pending={save.isPending}
        onConfirm={() => save.mutate('DEACTIVATED')}
        // Declining goes back to the status dialog with the choice and the
        // reason exactly as they were, and focus back on the button that asked.
        onCancel={() => setConfirming(false)}
      />
    </>
  );
}
